import { useCallback, useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { AccountType } from '@paladin-monorepo/common';
import type { CaseStudyDraftFromFileDto } from '@paladin-monorepo/common';
import { useAuth } from '@/lib/auth';
import { PersonalInfoCard } from '@/components/profile/PersonalInfoCard';
import { ExpertisesCard } from '@/components/profile/ExpertisesCard';
import { LanguageSkillsCard } from '@/components/profile/LanguageSkillsCard';
import { SkillsCard } from '@/components/profile/SkillsCard';
import { WorkCollaborationPreferencesCard } from '@/components/profile/WorkCollaborationPreferencesCard';
import { SupplierIndustriesCard } from '@/components/profile/SupplierIndustriesCard';
import { SupplierSeniorityCard } from '@/components/profile/SupplierSeniorityCard';
import { SupplierCertificationsCard } from '@/components/profile/SupplierCertificationsCard';
import {
  CaseStudiesCard,
  type CaseStudyCreatePrefill,
} from '@/components/profile/CaseStudiesCard';
import { ProfileCredentialsCard } from '@/components/profile/ProfileCredentialsCard';
import { ProfileCompletenessCard } from '@/components/profile/ProfileCompletenessCard';
import { ConnectedAccountsCard } from '@/pages/settings/ConnectedAccountsCard';
import { SectionAnchor, SectionNavigation } from '@/components/SectionNavigation';
import { useProfileSettingsSections } from '@/components/settings/useProfileSettingsSections';

export function ProfileSettingsTab() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const ref = searchParams.get('ref');
  const ssoToastShown = useRef(false);
  const { user } = useAuth({
    middleware: 'auth',
    redirectTo: '/',
    tmp: 'ProfileSettingsTab',
  });
  const isSupplierProfile =
    user?.account?.accountType === AccountType.Consultant ||
    user?.account?.accountType === AccountType.SolutionProvider;
  const profileSections = useProfileSettingsSections(isSupplierProfile);

  const [createPrefill, setCreatePrefill] = useState<CaseStudyCreatePrefill | null>(
    null,
  );

  const handleCaseStudyDraft = useCallback(
    (draft: CaseStudyDraftFromFileDto, file: File) => {
      setCreatePrefill({ draft, pendingFile: file });
    },
    [],
  );

  const handleCreatePrefillConsumed = useCallback(() => {
    setCreatePrefill(null);
  }, []);

  useEffect(() => {
    if (!ref || ssoToastShown.current) {
      return;
    }

    if (
      ref === 'linkedin-connected'
      || ref === 'linkedin-already-linked'
      || ref === 'google-connected'
      || ref === 'google-already-linked'
    ) {
      ssoToastShown.current = true;
      if (ref === 'linkedin-connected') {
        toast.success(t('settings.connectedAccounts.linkedin.connectedSuccess'));
      } else if (ref === 'linkedin-already-linked') {
        toast.error(t('settings.connectedAccounts.linkedin.alreadyLinked'));
      } else if (ref === 'google-connected') {
        toast.success(t('settings.connectedAccounts.google.connectedSuccess'));
      } else {
        toast.error(t('settings.connectedAccounts.google.alreadyLinked'));
      }
    }
  }, [ref, t]);

  return (
    <div className="container mx-auto">
      <div className="flex items-start gap-6">
        <aside className="hidden lg:block">
          <SectionNavigation sections={profileSections} />
        </aside>
        <div className="min-w-0 flex-1 space-y-6">
          <div>
            <h1 className="font-heading text-2xl font-semibold">{t('settings.tabs.profile')}</h1>
            <p className="mt-2 text-muted-foreground">{t('settings.overview.descriptions.profile')}</p>
          </div>
          <SectionAnchor id="connected-accounts">
            <ConnectedAccountsCard />
          </SectionAnchor>
          {isSupplierProfile && (
            <SectionAnchor id="profile-completeness">
              <ProfileCompletenessCard enabled={isSupplierProfile} />
            </SectionAnchor>
          )}
          <SectionAnchor id="personal-info">
            <PersonalInfoCard />
          </SectionAnchor>
          {isSupplierProfile && (
            <SectionAnchor id="profile-credentials">
              <ProfileCredentialsCard onCaseStudyDraft={handleCaseStudyDraft} />
            </SectionAnchor>
          )}
          <SectionAnchor id="expertises">
            <ExpertisesCard />
          </SectionAnchor>
          <SectionAnchor id="language-skills">
            <LanguageSkillsCard />
          </SectionAnchor>
          <SectionAnchor id="skills">
            <SkillsCard />
          </SectionAnchor>
          {isSupplierProfile && (
            <SectionAnchor id="collaboration-preferences">
              <WorkCollaborationPreferencesCard />
            </SectionAnchor>
          )}
          {isSupplierProfile && (
            <SectionAnchor id="industries">
              <SupplierIndustriesCard />
            </SectionAnchor>
          )}
          {isSupplierProfile && (
            <SectionAnchor id="seniority">
              <SupplierSeniorityCard />
            </SectionAnchor>
          )}
          {isSupplierProfile && (
            <SectionAnchor id="certifications">
              <SupplierCertificationsCard />
            </SectionAnchor>
          )}
          {isSupplierProfile && (
            <SectionAnchor id="case-studies">
              <CaseStudiesCard
                createPrefill={createPrefill}
                onCreatePrefillConsumed={handleCreatePrefillConsumed}
              />
            </SectionAnchor>
          )}
        </div>
      </div>
    </div>
  );
}
